import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { rolePermissions, roles } from './permissionsConfig';
import { apiFetch, sanitizeAuthUser, subscribeToUnauthorizedResponses } from 'api/client';

const AuthContext = createContext();

const SESSION_STATUS_INTERVAL_MS = 60 * 1000;
const SESSION_TOUCH_INTERVAL_MS = 5 * 60 * 1000;
const SESSION_TOUCH_RETRY_MS = 30 * 1000;
const SESSION_IDLE_MS = 60 * 60 * 1000;
const SESSION_EVENT_KEY = 'authSessionEvent';
const SESSION_ACTIVITY_KEY = 'authSessionActivity';
const DEFAULT_ORG_PREFERENCES = { flowx_enabled: false, flowx_url: '' };
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];

const normalizeRole = (value) => {
    const values = Array.isArray(value) ? value : String(value || '').split(/[\s,]+/);
    const normalized = values.map((item) => String(item || '').trim().toLowerCase()).filter(Boolean);
    if (normalized.includes(roles.admin)) return roles.admin;
    if (normalized.includes(roles.user)) return roles.user;
    return roles.guest;
};

const organizationValue = (organization) => organization?.id ?? organization?.name ?? '';

const selectRestoredOrganization = (data, previousSelection) => {
    const organizations = Array.isArray(data?.organizations) ? data.organizations : [];
    const allowedValues = new Set(organizations.map(organizationValue).filter(Boolean).map(String));
    const candidates = [data?.active_org_id, previousSelection, data?.default_org_id];

    for (const candidate of candidates) {
        if (candidate != null && candidate !== '' && allowedValues.has(String(candidate))) {
            return String(candidate);
        }
    }

    const firstOrganization = organizationValue(organizations[0]);
    return firstOrganization == null ? '' : String(firstOrganization);
};

const parseStoredUser = () => {
    try {
        const stored = localStorage.getItem('authUser');
        return stored ? sanitizeAuthUser(JSON.parse(stored)) : null;
    } catch {
        console.warn('Failed to parse stored authUser');
        return null;
    }
};

const parseSessionMetadata = (value) => {
    if (!value || typeof value !== 'object') return null;
    const idleExpiresAt = value.idle_expires_at;
    const absoluteExpiresAt = value.absolute_expires_at;
    if (!idleExpiresAt || !absoluteExpiresAt) return null;
    return { idle_expires_at: idleExpiresAt, absolute_expires_at: absoluteExpiresAt };
};

const parseStoredActivityTimestamp = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(SESSION_ACTIVITY_KEY));
        const touchedAt = Number(stored?.touched_at);
        return Number.isFinite(touchedAt) ? touchedAt : 0;
    } catch {
        return 0;
    }
};

const AuthProvider = ({ children }) => {
    const initialUserRef = useRef(parseStoredUser());
    const [authUser, setAuthUserState] = useState(initialUserRef.current);
    const [userRole, setUserRole] = useState(() => localStorage.getItem('userRole') || roles.guest);
    const [selectedOrganization, setSelectedOrganization] = useState(() => localStorage.getItem('selectedOrganization') || '');
    const [loading, setLoading] = useState(true);
    const [orgPreferences, setOrgPreferences] = useState(DEFAULT_ORG_PREFERENCES);
    const [sessionStatus, setSessionStatus] = useState(null);
    const [sessionEndReason, setSessionEndReason] = useState(null);
    const authUserRef = useRef(initialUserRef.current);
    const mountedRef = useRef(true);
    const statusPromiseRef = useRef(null);
    const touchPromiseRef = useRef(null);
    const lastTouchAtRef = useRef(parseStoredActivityTimestamp());
    const lastTouchAttemptAtRef = useRef(0);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const updateSessionMetadata = useCallback((value, broadcast = false) => {
        const metadata = parseSessionMetadata(value);
        if (!metadata) return;
        const idleExpiry = Date.parse(metadata.idle_expires_at);
        const sharedTouch = Number(value?.touched_at);
        const inferredTouch = broadcast
            ? Date.now()
            : Number.isFinite(sharedTouch)
                ? sharedTouch
                : idleExpiry - SESSION_IDLE_MS;
        if (Number.isFinite(inferredTouch)) {
            lastTouchAtRef.current = Math.max(lastTouchAtRef.current, inferredTouch);
        }
        if (mountedRef.current) setSessionStatus(metadata);
        if (broadcast) {
            localStorage.setItem(SESSION_ACTIVITY_KEY, JSON.stringify({
                ...metadata,
                touched_at: Date.now(),
            }));
        }
    }, []);

    const terminateSession = useCallback((reason = 'expired', broadcast = true) => {
        authUserRef.current = null;
        setAuthUserState(null);
        setUserRole(roles.guest);
        setSelectedOrganization('');
        setOrgPreferences(DEFAULT_ORG_PREFERENCES);
        setSessionStatus(null);
        setSessionEndReason(reason);
        lastTouchAtRef.current = 0;
        lastTouchAttemptAtRef.current = 0;
        localStorage.removeItem('authUser');
        localStorage.removeItem('userRole');
        localStorage.removeItem('selectedOrganization');
        localStorage.removeItem(SESSION_ACTIVITY_KEY);
        if (broadcast) {
            localStorage.setItem(SESSION_EVENT_KEY, JSON.stringify({ reason, timestamp: Date.now() }));
        }
    }, []);

    const setAuthUser = useCallback((value) => {
        setAuthUserState((previous) => {
            const nextValue = sanitizeAuthUser(typeof value === 'function' ? value(previous) : value);
            authUserRef.current = nextValue;
            if (nextValue) {
                setSessionEndReason(null);
                updateSessionMetadata(nextValue.session);
            }
            return nextValue;
        });
    }, [updateSessionMetadata]);

    const checkSession = useCallback(() => {
        if (!authUserRef.current) return Promise.resolve(null);
        if (statusPromiseRef.current) return statusPromiseRef.current;

        const request = apiFetch('/auth/session/status', { skipSessionCheck: true })
            .then(async (response) => {
                if (response.status === 401) {
                    terminateSession('expired');
                    return null;
                }
                if (!response.ok) return null;
                const metadata = await response.json();
                updateSessionMetadata(metadata);
                return metadata;
            })
            .catch(() => null)
            .finally(() => {
                if (statusPromiseRef.current === request) statusPromiseRef.current = null;
            });
        statusPromiseRef.current = request;
        return request;
    }, [terminateSession, updateSessionMetadata]);

    const touchSession = useCallback((force = false) => {
        if (!authUserRef.current) return Promise.resolve(null);
        if (touchPromiseRef.current) return touchPromiseRef.current;

        const now = Date.now();
        if (!force && now - lastTouchAtRef.current < SESSION_TOUCH_INTERVAL_MS) {
            return Promise.resolve(null);
        }
        if (now - lastTouchAttemptAtRef.current < SESSION_TOUCH_RETRY_MS) {
            return Promise.resolve(null);
        }
        lastTouchAttemptAtRef.current = now;

        const request = apiFetch('/auth/session/touch', { method: 'POST', skipSessionCheck: true })
            .then(async (response) => {
                if (response.status === 401) {
                    terminateSession('expired');
                    return null;
                }
                if (!response.ok) return null;
                const metadata = await response.json();
                updateSessionMetadata(metadata, true);
                return metadata;
            })
            .catch(() => null)
            .finally(() => {
                if (touchPromiseRef.current === request) touchPromiseRef.current = null;
            });
        touchPromiseRef.current = request;
        return request;
    }, [terminateSession, updateSessionMetadata]);

    const reportActivity = useCallback(() => {
        void touchSession(false);
    }, [touchSession]);

    const registerActivityFrame = useCallback((frame) => {
        if (!frame) return () => {};
        let frameDocument = null;
        const attachFrameListeners = () => {
            if (frameDocument) {
                ACTIVITY_EVENTS.forEach((eventName) => frameDocument.removeEventListener(eventName, reportActivity, true));
            }
            try {
                frameDocument = frame.contentDocument;
                if (!frameDocument) throw new Error('iframe document is not accessible');
                ACTIVITY_EVENTS.forEach((eventName) => frameDocument.addEventListener(eventName, reportActivity, { capture: true, passive: true }));
            } catch {
                frameDocument = null;
            }
        };
        const reportFallbackActivity = () => {
            if (!frameDocument) reportActivity();
        };
        frame.addEventListener('load', attachFrameListeners);
        frame.addEventListener('pointerenter', reportFallbackActivity);
        frame.addEventListener('focus', reportFallbackActivity);
        attachFrameListeners();
        return () => {
            frame.removeEventListener('load', attachFrameListeners);
            frame.removeEventListener('pointerenter', reportFallbackActivity);
            frame.removeEventListener('focus', reportFallbackActivity);
            if (frameDocument) {
                ACTIVITY_EVENTS.forEach((eventName) => frameDocument.removeEventListener(eventName, reportActivity, true));
            }
        };
    }, [reportActivity]);

    const logout = useCallback(async () => {
        const response = await apiFetch('/logout', { method: 'POST', skipSessionCheck: true });
        if (!response.ok) throw new Error(`Logout failed (${response.status})`);
        terminateSession('manual');
    }, [terminateSession]);

    useEffect(() => {
        let cancelled = false;
        async function restoreSession() {
            try {
                const response = await apiFetch('/auth/get-current-user', { skipSessionCheck: true });
                if (cancelled) return;
                if (response.status === 401) {
                    if (initialUserRef.current) terminateSession('expired');
                    else terminateSession(null, false);
                    return;
                }
                if (!response.ok) return;

                const data = sanitizeAuthUser(await response.json());
                authUserRef.current = data;
                setAuthUserState(data);
                setUserRole(normalizeRole(data.role || data.roles));
                setSelectedOrganization((previous) => selectRestoredOrganization(data, previous));
            } catch {
                // Preserve cached identity until the server definitively rejects it.
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        restoreSession();
        return () => { cancelled = true; };
    }, [terminateSession]);

    useEffect(() => {
        if (loading || !authUser) return undefined;
        void touchSession(false);
        return undefined;
    }, [authUser, loading, touchSession]);

    useEffect(() => subscribeToUnauthorizedResponses(() => {
        void checkSession();
    }), [checkSession]);

    useEffect(() => {
        if (!authUser) return undefined;
        const checkIfVisible = () => {
            if (document.visibilityState === 'visible') void checkSession();
        };
        const interval = window.setInterval(checkIfVisible, SESSION_STATUS_INTERVAL_MS);
        window.addEventListener('focus', checkIfVisible);
        document.addEventListener('visibilitychange', checkIfVisible);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', checkIfVisible);
            document.removeEventListener('visibilitychange', checkIfVisible);
        };
    }, [authUser, checkSession]);

    useEffect(() => {
        if (!authUser) return undefined;
        ACTIVITY_EVENTS.forEach((eventName) => document.addEventListener(eventName, reportActivity, { capture: true, passive: true }));
        return () => {
            ACTIVITY_EVENTS.forEach((eventName) => document.removeEventListener(eventName, reportActivity, true));
        };
    }, [authUser, reportActivity]);

    useEffect(() => {
        const handleStorage = (event) => {
            if (event.key === SESSION_EVENT_KEY && event.newValue) {
                try {
                    const payload = JSON.parse(event.newValue);
                    terminateSession(payload.reason === 'expired' ? 'expired' : 'remote', false);
                } catch {
                    terminateSession('remote', false);
                }
            }
            if (event.key === SESSION_ACTIVITY_KEY && event.newValue) {
                try {
                    updateSessionMetadata(JSON.parse(event.newValue));
                } catch {
                    // Ignore malformed cross-tab activity messages.
                }
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [terminateSession, updateSessionMetadata]);

    useEffect(() => {
        if (authUser) {
            try {
                localStorage.setItem('authUser', JSON.stringify(sanitizeAuthUser(authUser)));
            } catch (error) {
                console.error('Failed to store authUser', error);
            }
        } else {
            localStorage.removeItem('authUser');
        }
    }, [authUser]);

    useEffect(() => {
        if (userRole && userRole !== roles.guest) localStorage.setItem('userRole', userRole);
        else localStorage.removeItem('userRole');
    }, [userRole]);

    useEffect(() => {
        if (selectedOrganization) localStorage.setItem('selectedOrganization', selectedOrganization);
        else localStorage.removeItem('selectedOrganization');
    }, [selectedOrganization]);

    useEffect(() => {
        if (!selectedOrganization || !authUser) return;
        apiFetch(`/auth/organization/${selectedOrganization}/preferences`)
            .then((response) => (response.ok ? response.json() : Promise.reject()))
            .then((data) => setOrgPreferences({ flowx_enabled: !!data.flowx_enabled, flowx_url: data.flowx_url || '' }))
            .catch(() => setOrgPreferences(DEFAULT_ORG_PREFERENCES));
    }, [selectedOrganization, authUser]);

    const hasPermission = (permission) => rolePermissions[userRole]?.includes(permission);

    return (
        <AuthContext.Provider value={{
            authUser,
            setAuthUser,
            userRole,
            setUserRole,
            hasPermission,
            selectedOrganization,
            setSelectedOrganization,
            loading,
            orgPreferences,
            setOrgPreferences,
            sessionStatus,
            sessionEndReason,
            reportActivity,
            registerActivityFrame,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export { AuthContext, AuthProvider };
